import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import {
  Button,
  FormContentIsland,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  LocalizedError,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@razz-team/ui-web';
import { AccountAutocomplete } from '../../Autocomplete/AccountAutocomplete';
import { Route } from '../../../routes/account.statements';
import { useAccountsStatementMutation, useAccountsWithAccountQuery } from '../../../api/accounts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, differenceInCalendarDays, differenceInDays } from 'date-fns';
import { StatementListItem, StatementTitle, StatementType } from './StatementListItem';
import { StatementsDateRangeDialog } from './StatementsDateRangeDialog';
import { useBoolean } from 'usehooks-ts';
import { formatDate } from '@razz-team/utils';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';

const timeOptions = [
  {
    title: 'Last 30 days',
    value: 30,
  },
  {
    title: 'Last 60 days',
    value: 60,
  },
  {
    title: 'Last 90 days',
    value: 90,
  },
  {
    title: 'Last 365 days',
    value: 365,
  },
];

const getTimeOptions = (daysFromOpenning: number) => {
  const output = [];
  if (daysFromOpenning < 365) {
    output.push(...timeOptions.slice(0, 3));
  } else if (daysFromOpenning < 90) {
    output.push(...timeOptions.slice(0, 2));
  } else {
    output.push(...timeOptions.slice(0, 1));
  }
  output.push({
    title: `Whole period (${daysFromOpenning} days)`,
    value: daysFromOpenning,
  });
  return output;
};

const statementTypes: StatementType[] = [
  'balance',
  'avg_balance',
  'transactions',
  'balance_and_transactions',
  'csv',
];

export function StatementsPage() {
  const { t } = useTranslation();
  const { accountId } = Route.useSearch();
  const accountsQuery = useAccountsWithAccountQuery(accountId);
  const navigate = Route.useNavigate();
  const defaultAccountId = accountsQuery.data?.[0]?.id ?? undefined;
  const [days, setDays] = useState<string>('30');
  const [type, setType] = useState<StatementType>('avg_balance');
  const [customDaysRange, setCustomDaysRange] = useState<{
    from: Date;
    to: Date;
    differenceInDays: string;
  } | null>(null);
  const dateRangeDialogIsOpen = useBoolean();

  const statementMutation = useAccountsStatementMutation({ accountId });

  const selectedAccount = useMemo(
    () => accountsQuery.data?.find((account) => account.id === accountId, []),
    [accountId, accountsQuery.data],
  );

  const periodOptions: { title: string; value: number }[] = useMemo(() => {
    if (selectedAccount) {
      const daysFromOpenning = differenceInDays(new Date(), selectedAccount.openedAt);
      return getTimeOptions(daysFromOpenning);
    } else {
      return timeOptions;
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (!accountId && defaultAccountId) {
      navigate({
        search: (prev) => (prev.accountId ? prev : { accountId: defaultAccountId }),
        replace: true,
      });
    }
  }, [accountId, defaultAccountId, navigate]);

  const handleDaysChange = useCallback(
    (e: SelectChangeEvent) => {
      if (e.target.value === 'custom') {
        e.preventDefault();
        e.stopPropagation();
        dateRangeDialogIsOpen.setTrue();
      } else {
        setCustomDaysRange(null);
        setDays(e.target.value);
      }
    },
    [dateRangeDialogIsOpen],
  );

  const handleSetType = useCallback((e: SelectChangeEvent<StatementType>) => {
    setType(e.target.value as StatementType);
  }, []);

  const handleAccountChange = useCallback(
    (_: unknown, nextAccountId: string | null) => {
      navigate({ search: { accountId: nextAccountId ?? defaultAccountId } });
    },
    [navigate, defaultAccountId],
  );

  const handleClickCustomTimeRange = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dateRangeDialogIsOpen.setTrue();
  };

  const handleSetCustomDateRange = useCallback(({ from, to }: { from: Date; to: Date }) => {
    setDays('custom');
    const differenceInDays = differenceInCalendarDays(to, from);
    setCustomDaysRange({ from, to, differenceInDays: differenceInDays.toString() });
  }, []);

  const handleGenerateStatement = useCallback(() => {
    if (days === 'custom' && customDaysRange) {
      statementMutation.mutate({
        type,
        since: customDaysRange.from.toISOString(),
        until: customDaysRange.to.toISOString(),
      });
    } else {
      const since = addDays(new Date(), -days).toISOString();
      statementMutation.mutate({ type, since });
    }
  }, [type, days, statementMutation, customDaysRange]);

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={t('Statements')}
          parent={{
            title: selectedAccount?.name ?? selectedAccount?.balance.currency ?? t('Account'),
            loading: !selectedAccount,
            to: '/account/$accountId',
            params: { accountId },
          }}
        />
      }
      compact
    >
      <Grid size={12}>
        <FormContentIsland>
          <AccountAutocomplete
            label={t('Account')}
            loading={accountsQuery.isPending}
            accounts={accountsQuery.data}
            value={accountId}
            fullWidth
            onChange={handleAccountChange}
            disableClearable
            name="accountId"
          />
          <FormControl variant="outlined" error={Boolean(statementMutation.error)}>
            <InputLabel id="demo-simple-select-autowidth-label">{t('Type')}</InputLabel>
            <Select
              label={t('Type')}
              onChange={handleSetType}
              value={type}
              renderValue={(value) => <StatementTitle type={value} />}
            >
              {statementTypes.map((type) => (
                <MenuItem value={type} key={type}>
                  <StatementListItem type={type} />
                </MenuItem>
              ))}
            </Select>
            {statementMutation.error ? (
              <FormHelperText error>
                <LocalizedError error={statementMutation.error} />
              </FormHelperText>
            ) : null}
          </FormControl>
          {type !== 'balance' ? (
            <FormControl variant="outlined">
              <InputLabel id="demo-simple-select-autowidth-label">{t('Period')}</InputLabel>
              <Select label="Period" onChange={handleDaysChange} value={days}>
                {periodOptions.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    {t(option.title)}
                  </MenuItem>
                ))}
                <MenuItem onClick={handleClickCustomTimeRange} value="custom">
                  {customDaysRange
                    ? t('Custom date range ({{from}} - {{to}})', {
                        from: formatDate(customDaysRange?.from, 'date'),
                        to: formatDate(customDaysRange?.to, 'date'),
                      })
                    : t('Custom date range')}
                </MenuItem>
              </Select>
            </FormControl>
          ) : null}
          <Button
            variant="contained"
            size="large"
            onClick={handleGenerateStatement}
            loading={statementMutation.isPending}
          >
            {t('Confirm')}
          </Button>
        </FormContentIsland>
      </Grid>
      <StatementsDateRangeDialog
        open={dateRangeDialogIsOpen.value}
        onClose={dateRangeDialogIsOpen.setFalse}
        onSubmit={handleSetCustomDateRange}
      />
    </MainLayout>
  );
}
